import { RootState } from '../../store';
import { isEmpty } from '../../utils/helpers';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface Transaction {
  id: string;
  sender: string;
  assetIndex: number;
  amount: number;
  receiver: string;
}

export interface TransactionPayload {
  id: string;
  transactions: Transaction[];
}

export interface TransactionSet {
  [key: string]: TransactionPayload;
}

export interface TokenPayload {
  id: string;
  tokens: string[];
}

export interface TokenSet {
  [key: string]: TokenPayload;
}

export interface SubmitTxn {
  optIn: boolean;
  xfer: boolean;
  optOut: boolean;
}

interface TransactionState {
  optInTxns: TransactionSet;
  xferTxns: TransactionSet;
  optOutTxns: TransactionSet;
  trashTxns: TransactionSet;
  error?: null;
  showResponse: boolean;
  response: string;
  submitTxn: SubmitTxn;
  txnTokens: TokenSet;
}

const initialState: TransactionState = {
  optInTxns: {},
  optOutTxns: {},
  xferTxns: {},
  trashTxns: {},
  response: '',
  showResponse: false,
  error: null,
  submitTxn: {
    optOut: false,
    xfer: false,
    optIn: false,
  },
  txnTokens: {},
};

const transactionsSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    setTransactions: (state, action: PayloadAction<any>) => {
      const {
        optInTxs,
        xferTxs,
        optOutTxs,
      }: { optInTxs: TransactionSet; optOutTxs: TransactionSet; xferTxs: TransactionSet } =
        action.payload;
      state.optInTxns = optInTxs;
      state.xferTxns = xferTxs;
      state.optOutTxns = optOutTxs;
    },
    resetTransactions: (state) => {
      state = initialState;
    },
    resetTransactionsByAccount: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload;
      if (state.optInTxns?.[id]) {
        state.optInTxns[id].transactions = [];
        state.xferTxns[id].transactions = [];
        state.optOutTxns[id].transactions = [];
      }
    },
    deleteTransactionsByAccount: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload;

      delete state.optInTxns[id];
      delete state.xferTxns[id];
      delete state.optOutTxns[id];
    },
    setTxResponse: (state, action) => {
      const { showResponse, response } = action.payload;
      state.showResponse = showResponse;
      state.response = response;
    },
    setSubmitTxn: (
      state,
      action: PayloadAction<{
        key: 'optIn' | 'xfer' | 'optOut';
        value: boolean;
      }>
    ) => {
      const { key, value } = action.payload;
      state.submitTxn[key] = value;
    },
    addTxnToken: (state, action: PayloadAction<any>) => {
      const { id, index, token } = action.payload;
      if (state.txnTokens?.[id]?.tokens) {
        state.txnTokens[id].tokens[index] = token;
      } else {
        state.txnTokens[id] = { id, tokens: ['', token] };
      }
    },
  },
});

export const {
  setTransactions,
  resetTransactionsByAccount,
  deleteTransactionsByAccount,
  resetTransactions,
  setTxResponse,
  setSubmitTxn,
  addTxnToken,
} = transactionsSlice.actions;

export default transactionsSlice.reducer;

export const getOptOutTransactions = (state: RootState): Transaction[] | [] => {
  if (!isEmpty(state.transactions.optOutTxns)) {
    return Object.values(state.transactions.optOutTxns).reduce((acc, cur) => {
      return [...acc, ...cur.transactions];
    }, [] as Transaction[]);
  } else return [];
};

export const getOptInTransactions = (state: RootState): Transaction[] | [] => {
  if (!isEmpty(state.transactions.optInTxns)) {
    return Object.values(state.transactions.optInTxns).reduce((acc, cur) => {
      return [...acc, ...cur.transactions];
    }, [] as Transaction[]);
  } else return [];
};

export const getXferTransactions = (state: RootState): Transaction[] | [] => {
  if (!isEmpty(state.transactions.xferTxns)) {
    return Object.values(state.transactions.xferTxns).reduce((acc, cur) => {
      return [...acc, ...cur.transactions];
    }, [] as Transaction[]);
  } else return [];
};

export const getOptInTransactionsByAddress = (state: RootState, address: string) => {
  if (state.transactions.optInTxns?.[address]) {
    return (state.transactions.optInTxns?.[address].transactions).filter(Boolean);
  } else return;
};

export const getXferTransactionsByAddress = (state: RootState, address: string) => {
  if (state.transactions.xferTxns?.[address]) {
    return (state.transactions.xferTxns?.[address].transactions).filter(Boolean);
  } else return;
};

export const getOptOutTransactionsByAddress = (state: RootState, address: string) => {
  if (state.transactions.optOutTxns?.[address]) {
    return (state.transactions.optOutTxns?.[address].transactions).filter(Boolean);
  } else return;
};

export const getAllTransactions = (state: RootState) => {
  const optIns = !isEmpty(state.transactions.optInTxns)
    ? Object.values(state.transactions.optInTxns).reduce((acc, cur) => {
        return [...acc, ...cur.transactions];
      }, [] as Transaction[])
    : [];

  const xfers = !isEmpty(state.transactions.xferTxns)
    ? Object.values(state.transactions.xferTxns).reduce((acc, cur) => {
        return [...acc, ...cur.transactions];
      }, [] as Transaction[])
    : [];
  const optOuts = !isEmpty(state.transactions.optOutTxns)
    ? Object.values(state.transactions.optOutTxns).reduce((acc, cur) => {
        return [...acc, ...cur.transactions];
      }, [] as Transaction[])
    : [];

  return [...optIns, ...xfers, ...optOuts].filter(Boolean);
};

export const getTransactionLength = (state: RootState) => {
  return getAllTransactions(state).length;
};

export const getTxLengthArray = (state: RootState) => {
  return {
    optInLg: getOptInTransactions(state).length,
    xferLg: getXferTransactions(state).length,
    optOutLg: getOptOutTransactions(state).length,
  };
};
